import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';

export default forwardRef(function GetPaginaDiez(props, ref) {
  const { logoSrc } = props || {};
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);

  const draw = (opts = {}) => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!canvas) return resolve();

      const containerWidth = wrapper ? wrapper.clientWidth : 800;
      const width = Math.max(600, containerWidth);
      const height = Math.round(width * (8.5 / 11)); // landscape-ish
      const dpr = window.devicePixelRatio || 1;

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      // split ratio (left gray / right white)
      const leftRatio = typeof opts.leftRatio === 'number' ? opts.leftRatio : 0.4;
      const leftW = Math.round(width * leftRatio);
      const rightX = leftW;

      // left: gray
      ctx.fillStyle = opts.leftBg || '#ffffff';
      ctx.fillRect(0, 0, leftW, height);

      // right: white
      ctx.fillStyle = opts.rightBg || '#ffffff';
      ctx.fillRect(rightX, 0, width - rightX, height);

      // subtle divider
      ctx.fillStyle = opts.dividerColor || '#ffffff';
      ctx.fillRect(Math.max(0, rightX - 1), 16, 2, height - 32);

      // --- content on left (gray) side ---
      const leftPadding = typeof opts.leftPadding === 'number' ? opts.leftPadding : 32;
      let leftContentX = leftPadding;
      let leftContentY = 100;
      // draw square number (like "1.1") at the very left of the gray side and shift content right
      const squareSize = typeof opts.leftSquareSize === 'number' ? opts.leftSquareSize : 60;
      const sqX = leftPadding;
      const sqY = leftContentY;
      // square background
      ctx.save();
      ctx.fillStyle = opts.leftSquareBg || '#5fe7ffff';
      ctx.fillRect(sqX, sqY, squareSize, squareSize);
      // square number text
      ctx.fillStyle = opts.leftSquareColor || '#01090cff';
      ctx.font = `${opts.leftSquareFontSize || 40}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const tipoReporte = props.tipoReporte || opts.tipoReporte || 'semanal';
      ctx.fillText(opts.leftSquareText || (tipoReporte === 'mensual' ? '2.4' : '2.2'), sqX + squareSize / 2, sqY + squareSize / 2);
      ctx.restore();
      // move content start to the right of the square (plus a small gap)
      leftContentX = sqX + squareSize + (typeof opts.leftSquareGap === 'number' ? opts.leftSquareGap : 12);
      const leftMaxW = leftW - leftContentX - leftPadding;

      const wrapText = (c, text, x, y0, maxWidth, lh) => {
        const words = String(text).split(' ');
        let line = '';
        let yPos = y0;
        for (let n = 0; n < words.length; n++) {
          const test = line ? line + ' ' + words[n] : words[n];
          if (c.measureText(test).width > maxWidth && line) {
            c.fillText(line, x, yPos);
            line = words[n];
            yPos += lh;
          } else {
            line = test;
          }
        }
        if (line) c.fillText(line, x, yPos);
        return yPos + lh;
      };

      const justifyWrapText = (c, text, x, y0, maxWidth, lh) => {
        const words = String(text).split(/\s+/).filter(Boolean);
        const lines = [];
        let line = [];
        let lineWidth = 0;
        const spaceW = c.measureText(' ').width;
        for (let i = 0; i < words.length; i++) {
          const w = words[i];
          const wWidth = c.measureText(w).width;
          if (!line.length) {
            line = [w];
            lineWidth = wWidth;
          } else if (lineWidth + spaceW + wWidth <= maxWidth) {
            line.push(w);
            lineWidth += spaceW + wWidth;
          } else {
            lines.push({ words: line, width: lineWidth });
            line = [w];
            lineWidth = wWidth;
          }
        }
        if (line.length) lines.push({ words: line, width: lineWidth });

        let yPos = y0;
        for (let i = 0; i < lines.length; i++) {
          const ln = lines[i];
          if (i < lines.length - 1 && ln.words.length > 1) {
            const gaps = ln.words.length - 1;
            const extra = (maxWidth - ln.width) / gaps;
            let cx = x;
            for (let j = 0; j < ln.words.length; j++) {
              const w = ln.words[j];
              c.fillText(w, cx, yPos);
              cx += c.measureText(w).width + spaceW + extra;
            }
          } else {
            c.fillText(ln.words.join(' '), x, yPos);
          }
          yPos += lh;
        }
        return yPos;
      };

      // Provided content (fixed)
      const title = props.title || 'Causas del DFI Semana actual';
      const subTitle = props.subTitle || 'Tabla 2. Causas del desplazamiento forzado intraurbano semana actual.';
      const textoUno = props.textoUno || '';
      
      // title — use same font size as PaginaTres when provided via opts.pageThreeTitleSize or props.pageThreeTitleSize
      const titleSize = (typeof opts.leftTitleSize === 'number')
        ? opts.leftTitleSize
        : 45;
      ctx.fillStyle = opts.leftTitleColor || '#0f172a';
      ctx.font = `bold ${titleSize}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      leftContentY = wrapText(ctx, title, leftContentX, leftContentY, leftMaxW, Math.round(titleSize * 1.4));

      // subtitle (smaller, muted)
      ctx.fillStyle = opts.leftSubTitleColor || '#475569';
      ctx.font = `${opts.leftSubTitleSize || 30}px Inter, Arial, sans-serif`;
      leftContentY = wrapText(ctx, subTitle, leftContentX, leftContentY + 100, leftMaxW, Math.round((opts.leftSubTitleSize || 40) * 1.4));

      // spacer before paragraphs
      leftContentY += 12;

      // paragraph textoUno
      ctx.fillStyle = opts.leftTextColor || '#334155';
      ctx.font = `${opts.leftTextSize || 25}px Inter, Arial, sans-serif`;
      leftContentY = justifyWrapText(ctx, textoUno, leftContentX, leftContentY + 100, leftMaxW, Math.round((opts.leftTextSize || 25) * 1.6));

      const mainLogoSrc = opts.logoSrc || logoSrc;
      const logoPromises = [];

      // existing bottom-right logo (if provided) — load later together with left logo
      const mainLogoPromise = mainLogoSrc
        ? new Promise((res) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              try {
                const maxW = Math.min(200, Math.round(width * 0.18));
                const w = Math.min(maxW, img.width);
                const h = (img.height / img.width) * w;
                const lx = width - 16 - w;
                const ly = height - 16 - h;
                ctx.drawImage(img, lx, ly, w, h);
              } catch (e) { /* ignore */ }
              res();
            };
            img.onerror = () => res();
            img.src = mainLogoSrc;
          })
        : Promise.resolve();
      logoPromises.push(mainLogoPromise);

      // --- table area (supports two modes)
      const tableData = opts.tableData || props.tableData || [];
      const tableTitle = opts.tableTitle || props.tableTitle || '';
      const rightPadding = typeof opts.rightPadding === 'number' ? opts.rightPadding : 32;
      const rightContentX = rightX + rightPadding;
      const rightContentY = 32;
      const rightMaxW = (width - rightX) - rightPadding * 2;
      const tableAreaW = Math.max(200, rightMaxW);
      const tableX = rightContentX;
      const tableY = rightContentY + 120;

      // If data uses categoria/hecho/totalHogares, render simple row-per-entry table
      const isCategoriaTable = tableData.length && tableData[0].categoria !== undefined;
      const hasComuna = isCategoriaTable && tableData.some((r) => r && r.comuna !== undefined && String(r.comuna).trim() !== '');
      const rowH = Math.round((opts.tableRowHeight || 28));
      const headerH = Math.round(rowH * 1.1);

      // column widths
      let col0R, col1R, col2R, col3R, col4R;
      if (isCategoriaTable) {
        if (hasComuna) {
          col0R = Math.floor(tableAreaW * 0.16); // Comuna
          col1R = Math.floor(tableAreaW * 0.17); // Categoria
          col2R = Math.floor(tableAreaW * 0.35); // Hecho
          col3R = Math.floor(tableAreaW * 0.16); // Total hogares
          col4R = tableAreaW - col0R - col1R - col2R - col3R; // Total personas
        } else {
          col1R = Math.floor(tableAreaW * 0.15); // Categoria
          col2R = Math.floor(tableAreaW * 0.50); // Hecho
          col3R = Math.floor(tableAreaW * 0.175); // Total hogares
          col4R = tableAreaW - col1R - col2R - col3R; // Total personas
        }
      } else {
        col1R = Math.floor(tableAreaW * 0.2);
        col2R = Math.floor(tableAreaW * 0.6);
        col3R = tableAreaW - col1R - col2R;
      }

      // header
      ctx.save();
      ctx.fillStyle = opts.tableHeaderBg || '#0f172a';
      ctx.fillRect(tableX, tableY, tableAreaW, headerH);
      ctx.fillStyle = opts.tableHeaderColor || '#ffffff';
      ctx.font = `${opts.tableHeaderSize || 20}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      const hh = tableY + Math.round(headerH / 2);
      if (isCategoriaTable) {
        if (hasComuna) {
          ctx.fillText('Comuna', tableX + 8, hh);
          ctx.fillText('Categoria', tableX + col0R + 8, hh);
          ctx.fillText('Hecho', tableX + col0R + col1R + 8, hh);
          ctx.textAlign = 'center';
          const hogaresColCenterX = tableX + col0R + col1R + col2R + Math.round(col3R / 2);
          const personasColCenterX = tableX + col0R + col1R + col2R + col3R + Math.round(col4R / 2);
          ctx.fillText('Total hogares', hogaresColCenterX, hh);
          ctx.fillText('Total personas', personasColCenterX, hh);
        } else {
          ctx.fillText('Categoria', tableX + 8, hh);
          ctx.fillText('Hecho', tableX + col1R + 8, hh);
          ctx.textAlign = 'center';
          const hogaresColCenterX = tableX + col1R + col2R + Math.round(col3R / 2);
          const personasColCenterX = tableX + col1R + col2R + col3R + Math.round(col4R / 2);
          ctx.fillText('Total hogares', hogaresColCenterX, hh);
          ctx.fillText('Total personas', personasColCenterX, hh);
        }
      } else {
        ctx.fillText('Subregiones', tableX + 8, hh);
        ctx.fillText('Municipio', tableX + col1R + 8, hh);
        ctx.textAlign = 'center';
        const totalColCenterX = tableX + col1R + col2R + Math.round(col3R / 2);
        ctx.fillText('Total hogares', totalColCenterX, hh);
      }
      ctx.restore();

      // rows: simple iteration for categoria table, grouped logic kept for legacy shape
      let ry = tableY + headerH;
      const baseRowH = rowH;
      const rowFont = `${opts.tableRowSize || 20}px Inter, Arial, sans-serif`;
      const rowFontBold = `bold ${opts.tableRowSize || 20}px Inter, Arial, sans-serif`;
      ctx.font = rowFont;

      if (isCategoriaTable) {
        let totalHogaresSum = 0;
        let totalPersonasSum = 0;
        for (let i = 0; i < tableData.length; i++) {
          const r = tableData[i] || {};
          const comuna = String(r.comuna || '');
          const cat = String(r.categoria || '');
          const hecho = String(r.hecho || '');
          const totalHog = String(r.totalHogares || r.total || '');
          const totalPers = String(r.totalPersonas || '');
          const totalHogNum = parseInt(String(totalHog).replace(/\D/g, ''), 10) || 0;
          const totalPersNum = parseInt(String(totalPers).replace(/\D/g, ''), 10) || 0;
          totalHogaresSum += totalHogNum;
          totalPersonasSum += totalPersNum;

          ctx.font = rowFont;
          
          // wrap comuna text (optional)
          const comunaLines = [];
          if (hasComuna) {
            const comunaMaxW = col0R - 16;
            const comunaWords = comuna.split(' ');
            let comunaLine = '';
            for (let n = 0; n < comunaWords.length; n++) {
              const test = comunaLine ? comunaLine + ' ' + comunaWords[n] : comunaWords[n];
              if (ctx.measureText(test).width > comunaMaxW && comunaLine) {
                comunaLines.push(comunaLine);
                comunaLine = comunaWords[n];
              } else {
                comunaLine = test;
              }
            }
            if (comunaLine) comunaLines.push(comunaLine);
          }

          // wrap categoria text
          const catMaxW = (hasComuna ? col1R : col1R) - 16;
          const catWords = cat.split(' ');
          const catLines = [];
          let catLine = '';
          for (let n = 0; n < catWords.length; n++) {
            const test = catLine ? catLine + ' ' + catWords[n] : catWords[n];
            if (ctx.measureText(test).width > catMaxW && catLine) {
              catLines.push(catLine);
              catLine = catWords[n];
            } else {
              catLine = test;
            }
          }
          if (catLine) catLines.push(catLine);

          // wrap hecho text
          const hechoMaxW = (hasComuna ? col2R : col2R) - 16;
          const hechoWords = hecho.split(' ');
          const hechoLines = [];
          let hechoLine = '';
          for (let n = 0; n < hechoWords.length; n++) {
            const test = hechoLine ? hechoLine + ' ' + hechoWords[n] : hechoWords[n];
            if (ctx.measureText(test).width > hechoMaxW && hechoLine) {
              hechoLines.push(hechoLine);
              hechoLine = hechoWords[n];
            } else {
              hechoLine = test;
            }
          }
          if (hechoLine) hechoLines.push(hechoLine);

          const numLines = Math.max(1, catLines.length, hechoLines.length, hasComuna ? comunaLines.length : 1);
          const blockH = baseRowH * numLines;

          // row bg
          ctx.fillStyle = opts.tableRowBg || '#ffffff';
          ctx.fillRect(tableX, ry, tableAreaW, blockH);

          // columns text
          ctx.fillStyle = opts.tableRowColor || '#0f172a';
          ctx.font = rowFont;
          ctx.textAlign = 'left';
          // Comuna (optional) - multiline
          ctx.textBaseline = 'top';
          if (hasComuna) {
            const comunaStartY = ry + (blockH - (comunaLines.length * baseRowH)) / 2 + baseRowH / 4;
            for (let li = 0; li < comunaLines.length; li++) {
              ctx.fillText(comunaLines[li], tableX + 8, comunaStartY + li * baseRowH);
            }
          }

          // Categoria (left) - multiline
          const catStartY = ry + (blockH - (catLines.length * baseRowH)) / 2 + baseRowH / 4;
          const catX = hasComuna ? (tableX + col0R + 8) : (tableX + 8);
          for (let li = 0; li < catLines.length; li++) {
            ctx.fillText(catLines[li], catX, catStartY + li * baseRowH);
          }
          // Hecho (middle) - multiline
          const hechoStartY = ry + (blockH - (hechoLines.length * baseRowH)) / 2 + baseRowH / 4;
          const hechoX = hasComuna ? (tableX + col0R + col1R + 8) : (tableX + col1R + 8);
          for (let li = 0; li < hechoLines.length; li++) {
            ctx.fillText(hechoLines[li], hechoX, hechoStartY + li * baseRowH);
          }
          // Total hogares (centered)
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          const hogaresColCenterX = hasComuna
            ? tableX + col0R + col1R + col2R + Math.round(col3R / 2)
            : tableX + col1R + col2R + Math.round(col3R / 2);
          ctx.fillText(totalHog, hogaresColCenterX, ry + blockH / 2);
          // Total personas (centered)
          const personasColCenterX = hasComuna
            ? tableX + col0R + col1R + col2R + col3R + Math.round(col4R / 2)
            : tableX + col1R + col2R + col3R + Math.round(col4R / 2);
          ctx.fillText(totalPers, personasColCenterX, ry + blockH / 2);

          // bottom border for row
          ctx.strokeStyle = opts.tableBorderColor || 'rgba(15,23,42,0.06)';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(tableX, ry + blockH - 1);
          ctx.lineTo(tableX + tableAreaW, ry + blockH - 1);
          ctx.stroke();

          ry += blockH;
        }

        if (tableData.length > 0) {
          const totalRowH = baseRowH;
          ctx.fillStyle = opts.tableTotalRowBg || '#ffffff';
          ctx.fillRect(tableX, ry, tableAreaW, totalRowH);
          ctx.fillStyle = opts.tableTotalLabelColor || '#0f172a';
          ctx.font = rowFontBold;
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          const totalLabelX = tableX + 8;
          ctx.fillText('Total', totalLabelX, ry + totalRowH / 2);

          ctx.textAlign = 'center';
          const hogaresColCenterX = hasComuna
            ? tableX + col0R + col1R + col2R + Math.round(col3R / 2)
            : tableX + col1R + col2R + Math.round(col3R / 2);
          const personasColCenterX = hasComuna
            ? tableX + col0R + col1R + col2R + col3R + Math.round(col4R / 2)
            : tableX + col1R + col2R + col3R + Math.round(col4R / 2);
          ctx.fillText(String(totalHogaresSum), hogaresColCenterX, ry + totalRowH / 2);
          ctx.fillText(String(totalPersonasSum), personasColCenterX, ry + totalRowH / 2);

          ctx.strokeStyle = opts.tableBorderColor || 'rgba(15,23,42,0.06)';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(tableX, ry + totalRowH - 1);
          ctx.lineTo(tableX + tableAreaW, ry + totalRowH - 1);
          ctx.stroke();

          ry += totalRowH;
        }
      } else {
        // legacy grouped table (kept as before)
        const groupMap = {};
        for (let i = 0; i < (opts.tableData || props.tableData || []).length; i++) {
          const r = (opts.tableData || props.tableData)[i] || {};
          const sub = String(r.subregion || '').trim();
          const muni = String(r.municipio || '').trim();
          const total = parseInt(String(r.totalHogares || '0').replace(/\D/g, ''), 10) || 0;
          if (!groupMap[sub]) groupMap[sub] = { subregion: sub, municipios: {} };
          if (!groupMap[sub].municipios[muni]) groupMap[sub].municipios[muni] = 0;
          groupMap[sub].municipios[muni] += total;
        }
        const grouped = Object.keys(groupMap)
          .map(k => ({
            subregion: groupMap[k].subregion,
            municipios: Object.keys(groupMap[k].municipios).map(m => ({ municipio: m, total: groupMap[k].municipios[m] }))
          }))
          .sort((a, b) => a.subregion.localeCompare(b.subregion, undefined, { sensitivity: 'base' }));

        for (let gi = 0; gi < grouped.length; gi++) {
          const row = grouped[gi];
          const municipios = row.municipios || [];
          const lines = Math.max(1, municipios.length);
          const totalRowCount = lines + 1;
          const blockH = baseRowH * totalRowCount;

          ctx.fillStyle = opts.tableRowBg || '#ffffff';
          ctx.fillRect(tableX, ry, tableAreaW, blockH);

          ctx.fillStyle = opts.tableRowColor || '#0f172a';
          ctx.textBaseline = 'middle';
          ctx.font = rowFont;
          const subCenterY = ry + Math.round(blockH / 2);
          ctx.fillText(String(row.subregion || ''), tableX + 8, subCenterY);

          const muniX = tableX + col1R + 8;
          const totalColCenterX = tableX + col1R + col2R + Math.round(col3R / 2);
          let subtotal = 0;
          for (let mi = 0; mi < municipios.length; mi++) {
            const m = municipios[mi];
            const muniText = m.municipio || '';
            const mTotal = typeof m.total === 'number' ? m.total : parseInt(String(m.total || '0').replace(/\D/g, ''), 10) || 0;
            subtotal += mTotal;

            const lineMidY = ry + mi * baseRowH + Math.round(baseRowH / 2);
            ctx.font = rowFont;
            ctx.textAlign = 'left';
            ctx.fillText(muniText, muniX, lineMidY);
            ctx.textAlign = 'center';
            ctx.fillText(String(mTotal), totalColCenterX, lineMidY);
            ctx.textAlign = 'left';
          }

          const totalRowY = ry + lines * baseRowH;
          ctx.fillStyle = opts.tableTotalRowBg || '#ffffff';
          ctx.fillRect(tableX, totalRowY, tableAreaW, baseRowH);
          ctx.fillStyle = opts.tableTotalLabelColor || '#0f172a';
          ctx.font = rowFont;
          ctx.textBaseline = 'middle';
          const totalLabelX = muniX;
          const totalMidY = totalRowY + Math.round(baseRowH / 2);
          ctx.fillText('Total', totalLabelX, totalMidY);
          ctx.font = rowFontBold;
          ctx.textAlign = 'center';
          ctx.fillText(String(subtotal), totalColCenterX, totalMidY);
          ctx.textAlign = 'left';
          ctx.font = rowFont;

          ctx.strokeStyle = opts.tableBorderColor || 'rgba(15,23,42,0.06)';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(tableX, ry + blockH - 1);
          ctx.lineTo(tableX + tableAreaW, ry + blockH - 1);
          ctx.stroke();

          ry += blockH;
        }
      }

      // outer table border and vertical separators
      try {
        const tableHeight = Math.max(1, ry - tableY);
        ctx.strokeStyle = opts.tableBorderColor || 'rgba(15,23,42,0.06)';
        ctx.lineWidth = 2;
        ctx.strokeRect(tableX + 1, tableY + 1, tableAreaW - 2, tableHeight - 2);
        const sep1 = Math.round(tableX + col1R);
        const sep2 = Math.round(tableX + col1R + col2R);
        ctx.beginPath();
        if (isCategoriaTable && hasComuna) {
          const s1 = Math.round(tableX + col0R);
          const s2 = Math.round(tableX + col0R + col1R);
          const s3 = Math.round(tableX + col0R + col1R + col2R);
          const s4 = Math.round(tableX + col0R + col1R + col2R + col3R);
          ctx.moveTo(s1 + 0.5, tableY);
          ctx.lineTo(s1 + 0.5, tableY + tableHeight);
          ctx.moveTo(s2 + 0.5, tableY);
          ctx.lineTo(s2 + 0.5, tableY + tableHeight);
          ctx.moveTo(s3 + 0.5, tableY);
          ctx.lineTo(s3 + 0.5, tableY + tableHeight);
          ctx.moveTo(s4 + 0.5, tableY);
          ctx.lineTo(s4 + 0.5, tableY + tableHeight);
        } else {
          ctx.moveTo(sep1 + 0.5, tableY);
          ctx.lineTo(sep1 + 0.5, tableY + tableHeight);
          ctx.moveTo(sep2 + 0.5, tableY);
          ctx.lineTo(sep2 + 0.5, tableY + tableHeight);
          if (isCategoriaTable) {
            const sep3 = Math.round(tableX + col1R + col2R + col3R);
            ctx.moveTo(sep3 + 0.5, tableY);
            ctx.lineTo(sep3 + 0.5, tableY + tableHeight);
          }
        }
        ctx.stroke();
      } catch (e) { /* ignore */ }      

      // wait for logos to load then resolve draw()
      Promise.all(logoPromises).then(() => resolve());
    });
  };

  const download = (filename) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = filename || `pagina_cuatro_${new Date().toISOString().slice(0,10)}.png`;
    a.click();
  };

  useImperativeHandle(ref, () => ({
    draw,
    download,
    getDataUrl: () => {
      const canvas = canvasRef.current;
      return canvas ? canvas.toDataURL('image/png') : null;
    }
  }), [draw]);

  useEffect(() => {
    draw();
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={wrapperRef} style={{ width: '100%' }}>
      <canvas ref={canvasRef} style={{ width: '100%', display: 'block', borderRadius: 6 }} />
    </div>
  );
});